/**
 * Card (Question) schemas
 * Consolidated from useCardData.ts and constants.ts
 */

import { z } from 'zod';
import { COLLECTION_DISPLAY } from '../../constants';
import { fieldMetadataSchema } from './database';
import { datasetQuerySchema } from './query';

// ============================================================================
// Card Schemas
// ============================================================================

/**
 * Full card schema with result_metadata
 * Used by GET /api/card/:id
 */
export const fullCardSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  display: z.enum(COLLECTION_DISPLAY),
  database_id: z.number().optional(),
  table_id: z.number().nullable().optional(),
  collection_id: z.number().nullable(),
  dataset_query: datasetQuerySchema,
  result_metadata: z.array(fieldMetadataSchema).nullable().optional(),
  visualization_settings: z.record(z.unknown()).optional(),
  parameters: z.array(z.unknown()).optional(),
  parameter_mappings: z.array(z.unknown()).optional(),
  updated_at: z.string(),
  created_at: z.string().optional(),
});

export type FullCard = z.infer<typeof fullCardSchema>;

/**
 * Card schema for collections
 * Used in collection items lists
 */
export const cardSchema = z.object({
  id: z.number(),
  dataset_query: z.record(z.unknown()),
  display: z.enum(COLLECTION_DISPLAY),
  database_id: z.number().optional(),
  collection_id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  updated_at: z.string(),
  visualization_settings: z
    .object({
      elis_summary: z.string().optional(),
      queues: z.string().array().optional(),
      snapshot_card_id: z.number().optional(),
      previous_suggest: z
        .object({
          name: z.string(),
          description: z.string(),
          display: z.string(),
          visualization_settings: z.record(z.unknown()).optional(),
          dataset_query: z.record(z.unknown()).optional(),
        })
        .optional(),
      'graph.x_axis.title_text': z.string().optional(),
      'graph.y_axis.title_text': z.string().optional(),
    })
    .passthrough()
    .optional(),
});

export type Card = z.infer<typeof cardSchema>;

/**
 * Card query status schema
 *
 * `error` is intentionally `unknown`: a failed Metabase query can return the
 * error as a string OR a structured object, and we only ever stringify it for
 * the auto-fix prompt. Keeping it strict (string-only) made valid failure
 * responses fail validation, which silently disabled the retry/auto-fix flow.
 */
export const cardQuerySchema = z.object({
  status: z.string(),
  error: z.unknown().optional(),
  error_type: z.string().optional(),
  stacktrace: z.unknown(),
});

export type CardQueryStatus = z.infer<typeof cardQuerySchema>;
